import React from "react";
import { Link } from "react-router-dom";

/**
 * Public legal pages. Routes: /legal/terms, /legal/privacy,
 * /legal/cancellation, /legal/refund. Version 2026-03-1 (27 Jul 2026).
 */

const Frame = ({ title, testId, children }) => (
  <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid={testId}>
    <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded p-6">
      <div className="mb-4 pb-4 border-b border-slate-200 text-xs text-slate-600">
        <Link to="/legal/terms" className="mr-4 hover:underline" data-testid="legal-link-terms">Terms &amp; Conditions</Link>
        <Link to="/legal/privacy" className="mr-4 hover:underline" data-testid="legal-link-privacy">Privacy Policy</Link>
        <Link to="/legal/cancellation" className="mr-4 hover:underline" data-testid="legal-link-cancellation">Cancellation Policy</Link>
        <Link to="/legal/refund" className="hover:underline" data-testid="legal-link-refund">Refund / Payment Policy</Link>
      </div>
      <h1 className="text-3xl font-black tracking-tight mb-1">{title}</h1>
      <div className="text-xs text-slate-500 mb-6">Effective 27 July 2026 · Version 2026-03-1</div>
      <div className="prose prose-slate max-w-none text-sm leading-relaxed space-y-3">
        {children}
      </div>
      <div className="mt-8 pt-4 border-t border-slate-200 text-[11px] text-slate-500 italic">
        Disclaimer: This document is a good-faith draft prepared for a small South African SaaS business. Before publishing amendments we recommend review by a South African attorney familiar with the ECT Act, POPIA, CPA and SARS tax practice.
      </div>
    </div>
  </div>
);

const H = ({ children }) => <h2 className="text-lg font-bold mt-5 mb-2">{children}</h2>;

export function TermsPage() {
  return (
    <Frame title="Terms of Service" testId="legal-terms-page">
      <H>1. Who we are</H>
      <p>TruckStop SaaS (“we”, “us”, “the Platform”) is operated by <b>Willem Johannes Bronkhorst, trading as TruckStop SaaS</b>, a sole proprietor in the Republic of South Africa. Company registration: <b>Not applicable — sole proprietor</b>.</p>
      <p>Physical address: <b>253 Lyster Street, Golfpark, Mookgophong, Limpopo, 0560, South Africa</b>.</p>
      <p>Contact: <b>bronkhorst09@gmail.com</b>.</p>

      <H>2. What the Platform is (and is not)</H>
      <p>TruckStop SaaS provides <b>software only</b>. We give truck-stop businesses (“Tenants”) a web application to run their operations — recording truck arrivals, capturing services rendered, issuing receipts and, on the financial plan, managing customer invoicing.</p>
      <p><b>We do not sell fuel, parking, wash, laundry, showers, or any physical service to end customers.</b> Tenants set their own prices, provide the services, issue their own invoices to their own customers, and are solely responsible for the accuracy, tax treatment and legality of every transaction they process using the Platform.</p>

      <H>3. Your account</H>
      <p>To use the Platform you must be at least 18 years old and legally authorised to bind the business you register on behalf of. You are responsible for keeping your login credentials confidential and for all activity performed under your account.</p>

      <H>4. Subscriptions &amp; billing</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>The Platform is offered as a <b>monthly subscription</b> at prices displayed at sign-up.</li>
        <li>Payment methods: <b>Manual EFT</b> or <b>PayFast</b> (once-off and recurring debit orders where offered).</li>
        <li>Recurring PayFast billing requires your explicit acceptance of the current version of these Terms before it is enabled.</li>
        <li>Failed payment triggers a <b>7-day grace period</b>. If unpaid at the end of grace, your account is suspended. Reactivation happens automatically once payment is received.</li>
      </ul>

      <H>5. Your responsibilities as a Tenant</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>Keep your business, tax, banking and contact information current on the Platform.</li>
        <li>Use the Platform only for lawful truck-stop operations.</li>
        <li>Do not attempt to bypass authentication, tamper with data belonging to other Tenants, or reverse-engineer the Platform.</li>
        <li>You are responsible for the customer data you upload, for lawful processing of it, and for honouring your own agreements with your customers.</li>
      </ul>

      <H>6. Data ownership</H>
      <p>You retain ownership of your operational data (your customers, vehicles, orders, invoices, ledger entries and uploaded documents). We process this data on your behalf to operate the Platform. See our <Link to="/legal/privacy" className="text-blue-700 hover:underline">Privacy Policy</Link> for full details.</p>

      <H>7. Service availability</H>
      <p>We aim to provide continuous availability but do not guarantee uninterrupted or error-free operation. Scheduled maintenance and unforeseen outages may occur. Our sole obligation in the event of downtime is set out in the <Link to="/legal/refund" className="text-blue-700 hover:underline">Refund / Payment Policy</Link>.</p>

      <H>8. Limitation of liability</H>
      <p>To the maximum extent permitted by law:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>We are not liable for indirect, consequential, incidental or punitive damages, loss of profits, loss of goodwill, or loss of data.</li>
        <li>Our total aggregate liability to you in any 12-month period is limited to the subscription fees actually paid by you to us in the <b>three (3) months immediately preceding</b> the event giving rise to the claim.</li>
        <li>We are not liable for any dispute between a Tenant and its customers. Any such dispute is between the Tenant and its customer only.</li>
      </ul>

      <H>9. Termination</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>You may cancel your subscription at any time (see <Link to="/legal/cancellation" className="text-blue-700 hover:underline">Cancellation Policy</Link>).</li>
        <li>We may suspend or terminate your account with reasonable notice if you materially breach these Terms, fail to pay after grace, or use the Platform unlawfully.</li>
        <li>On termination, your access ends at the end of your paid period. Data handling after termination is set out in the Privacy Policy.</li>
      </ul>

      <H>10. Changes to these Terms</H>
      <p>We may update these Terms from time to time. Material changes will be communicated through the Platform or by email. Continued use after 14 days of notice constitutes acceptance. For recurring PayFast subscriptions, we will require you to re-accept the latest version before your next automatic debit.</p>

      <H>11. Governing law</H>
      <p>These Terms are governed by the laws of the <b>Republic of South Africa</b>. Disputes are subject to the exclusive jurisdiction of the South African courts.</p>

      <H>12. Contact</H>
      <p>For questions about these Terms: <b>bronkhorst09@gmail.com</b>.</p>
    </Frame>
  );
}

export function PrivacyPage() {
  return (
    <Frame title="Privacy Policy" testId="legal-privacy-page">
      <H>1. Who is responsible for your data</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>For <b>your Tenant account information</b> (your business, its owners, admins and staff) we are the <b>Responsible Party</b> (Data Controller) under POPIA.</li>
        <li>For <b>operational data you upload to run your truck stop</b> (your customers, vehicles, orders, invoices, ledger, receipt proofs) we act as an <b>Operator</b> (Data Processor) on your behalf. You are the Responsible Party for that data.</li>
      </ul>
      <p>The Platform is operated by <b>Willem Johannes Bronkhorst, trading as TruckStop SaaS</b>, a sole proprietor in the Republic of South Africa, with physical address at <b>253 Lyster Street, Golfpark, Mookgophong, Limpopo, 0560, South Africa</b>.</p>

      <H>2. What we process</H>
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Account data</b>: names, email addresses, phone numbers, roles, hashed passwords.</li>
        <li><b>Tenant data</b>: business name, trading name, VAT number, contact details, banking details, subscription plan and status.</li>
        <li><b>Operational data uploaded by Tenants</b>: customer names and contact info, vehicle registration numbers, order records, uploaded photos (receipt/registration proof) and PDFs (proof of payment, invoices, credit notes, statements).</li>
        <li><b>Financial data</b>: ledger entries, invoices, credit notes, payments received, VAT status.</li>
        <li><b>Technical data</b>: IP addresses at the point of authentication, JWT session tokens, audit-log entries recording who did what and when.</li>
        <li><b>PayFast payment data</b>: tokens returned by PayFast for recurring billing. <b>We never see or store full card numbers.</b></li>
      </ul>

      <H>3. Why we process it</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>To provide and operate the Platform.</li>
        <li>To bill Tenants for their subscription and process payments.</li>
        <li>To meet legal obligations (tax and financial record retention).</li>
        <li>To keep the Platform secure (audit logs, brute-force protection).</li>
        <li>To respond to support enquiries.</li>
      </ul>

      <H>4. Legal basis</H>
      <p>We rely on contractual necessity, legitimate interest (fraud prevention, product improvement, security) and, where required, your explicit consent.</p>

      <H>5. How long we keep your data</H>
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Active Tenants</b>: for as long as your account is active.</li>
        <li><b>After cancellation</b>: 90 days of read-only grace, then archived for 12 months for re-activation or export requests.</li>
        <li><b>Financial records</b> (invoices, ledger entries, payments): retained for at least <b>5 years</b> to comply with the South African Tax Administration Act.</li>
        <li><b>Audit logs</b>: retained for at least 12 months.</li>
        <li><b>Uploaded receipt/POP files</b>: retained for the same period as the transaction they support.</li>
      </ul>

      <H>6. Where your data lives</H>
      <p>Our database is hosted on managed infrastructure. File uploads (receipts, POPs, PDFs) are stored on <b>Cloudflare R2</b> under a private bucket only our servers can access. Payment initiation and card processing happen inside PayFast&apos;s environment. We only receive success/failure notifications and a tokenised reference for recurring debits.</p>

      <H>7. Sharing</H>
      <p>We do not sell your personal information. We share it only:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>With sub-processors that operate the Platform (hosting, R2 storage, PayFast, email delivery when notification services go live). Cloudflare and PayFast both hold POPIA-aligned processor agreements.</li>
        <li>With law enforcement or regulators when legally compelled.</li>
      </ul>

      <H>8. Your rights under POPIA</H>
      <p>You may:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Ask what personal information we hold about you.</li>
        <li>Correct or update it.</li>
        <li>Request deletion (subject to any legal retention obligation).</li>
        <li>Object to processing.</li>
        <li>Lodge a complaint with the <b>Information Regulator of South Africa</b> (<a href="https://inforegulator.org.za" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">inforegulator.org.za</a>).</li>
      </ul>
      <p>To exercise any right, email our Information Officer at <b>bronkhorst09@gmail.com</b>.</p>

      <H>9. Security</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>All connections use HTTPS/TLS.</li>
        <li>Passwords are hashed with bcrypt.</li>
        <li>Access is enforced by role-based permissions and per-tenant data scoping.</li>
        <li>File storage is encrypted at rest.</li>
        <li>Every mutation writes an audit log.</li>
      </ul>

      <H>10. Cookies &amp; tokens</H>
      <p>We use short-lived JWT tokens to keep you signed in. We do not use tracking cookies for advertising.</p>

      <H>11. Information Officer</H>
      <p><b>Name</b>: Willem Johannes Bronkhorst<br/><b>Email</b>: bronkhorst09@gmail.com</p>

      <H>12. Changes to this policy</H>
      <p>Material changes will be notified through the Platform. The effective date and version at the top of this policy are always current.</p>
    </Frame>
  );
}

export function CancellationPolicyPage() {
  return (
    <Frame title="Cancellation Policy" testId="legal-cancellation-page">
      <H>1. How to cancel</H>
      <p>You can cancel your subscription at any time from <b>Truck Stop App → Settings → Cancel Subscription</b>. Confirmation is required.</p>

      <H>2. What happens next</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>Future PayFast recurring debit orders are stopped immediately.</li>
        <li>Future SaaS invoices are not issued.</li>
        <li>Your account remains fully accessible until the <b>end of the current paid billing cycle</b>.</li>
        <li>After the end of that cycle, your account moves to a <b>90-day read-only grace state</b>. During grace you can log in, download historical documents, and reactivate at any time.</li>
        <li>After the 90-day grace, your account is archived. Reactivation is possible for up to <b>12 months</b>; we will export your data on request during that window.</li>
      </ul>

      <H>3. What we do not refund</H>
      <p>We do not proactively refund the unused portion of the current billing cycle. This aligns with standard month-to-month SaaS terms. Refund exceptions are set out in the <Link to="/legal/refund" className="text-blue-700 hover:underline">Refund / Payment Policy</Link>.</p>

      <H>4. Recurring billing</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>Cancelling stops future PayFast charges — no further debit orders will be attempted.</li>
        <li>If a PayFast attempt is already in flight at the time of cancellation, it will be processed. Any resulting payment will apply to the invoice it was raised for.</li>
      </ul>

      <H>5. Your data after cancellation</H>
      <p>See our <Link to="/legal/privacy" className="text-blue-700 hover:underline">Privacy Policy</Link> for retention periods. In summary: 90-day read-only grace → 12-month archive with export on request → deletion, subject to legal retention obligations for financial records (5 years for invoices, ledger, payments).</p>

      <H>6. Contact</H>
      <p><b>bronkhorst09@gmail.com</b></p>
    </Frame>
  );
}

export function RefundPolicyPage() {
  return (
    <Frame title="Refund / Payment Policy" testId="legal-refund-page">
      <H>1. Accepted payment methods</H>
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Manual EFT</b>: deposit into our nominated bank account and upload your proof of payment.</li>
        <li><b>PayFast one-off</b>: instant online payment.</li>
        <li><b>PayFast recurring debit order</b>: automatic monthly debit for tenants who opt in.</li>
      </ul>
      <p><b>All prices are in South African Rand (ZAR). We are not currently VAT registered, and VAT is not charged on subscription fees unless stated otherwise.</b></p>

      <H>2. When we bill</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>Manual EFT: an invoice is issued at the start of each billing cycle. Payment is due within <b>7 days</b>.</li>
        <li>PayFast one-off: pay on demand from your Platform.</li>
        <li>PayFast recurring: debited automatically on your billing anniversary.</li>
      </ul>

      <H>3. Failed payments</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>A failed or missed payment triggers a <b>7-day grace period</b>.</li>
        <li>During grace, your account remains fully active and you will receive reminder notifications.</li>
        <li>If unpaid at the end of grace, your account is <b>suspended</b>. Read-only access to your existing data is preserved. You can reactivate at any time by paying the outstanding invoice.</li>
      </ul>

      <H>4. Refunds — general rule</H>
      <p><b>Subscription fees are non-refundable</b> once the billing period has started. This applies to all payment methods.</p>

      <H>5. Refunds — exceptions</H>
      <p>We will refund a subscription payment in the following cases only:</p>
      <ol className="list-decimal pl-6 space-y-1">
        <li><b>Duplicate payment</b> for the same invoice.</li>
        <li><b>Verified Platform outage</b> exceeding <b>24 hours of continuous platform unavailability</b> within a single billing period, in which case a pro-rated refund equal to the affected days is issued.</li>
        <li><b>Payment made in error</b> (e.g. a subscription paid after cancellation but before the cancellation was processed on our side).</li>
      </ol>

      <H>6. How to request a refund</H>
      <p>Email <b>bronkhorst09@gmail.com</b> within <b>30 days</b> of the payment date with:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Your tenant slug / business name</li>
        <li>The invoice number</li>
        <li>Proof of the duplicate or erroneous payment (or a description of the outage)</li>
      </ul>
      <p>We will respond within <b>5 business days</b> and, if approved, issue the refund within <b>10 business days after approval</b> via the original payment method.</p>

      <H>7. Chargebacks</H>
      <p>If you dispute a charge, we ask you to contact <b>bronkhorst09@gmail.com</b> first — most cases are resolved without a chargeback. Chargebacks filed without prior contact may result in account suspension until the matter is resolved.</p>

      <H>8. VAT invoices</H>
      <p>We are not currently VAT registered. Standard receipts for every payment are available inside the Platform under <b>Billing → Invoices</b>. If we register for VAT in future, VAT-compliant invoices will be issued from the registration date onward.</p>

      <H>9. Contact</H>
      <p><b>bronkhorst09@gmail.com</b></p>
    </Frame>
  );
}
